import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { CompanySuggestions, CompanyWrite } from "../../lib/crm-types";

/**
 * What the register knows about the company being typed.
 *
 * Offered, never applied silently. Every value carries where it came from and
 * has to be accepted, because the two sources are wrong in different ways: a
 * credit bureau confidently returns the wrong company for a shared domain, and
 * the neighbours only tell you how *they* were classified. A form that filled
 * itself in from either would produce records nobody had actually checked,
 * which is worse than the blank fields it replaced.
 *
 * Two halves, and they answer different questions. **The candidate list** is
 * "which company is this?" -- searched for by whatever the form can offer: the
 * name if somebody has typed one, otherwise a name guessed from the domain,
 * because no register maps a British domain to a company. Picking one fills the
 * form from that company. **The field chips** below are "what do we already
 * know?", from the other companies we hold on the same domain.
 *
 * Watching the name as well as the domain is what makes it useful twice over.
 * `sparks.co.uk` guesses "sparks" and matches five thousand companies, and
 * typing "Sparks Electrical" is the only thing that narrows it; a sole trader
 * with no website has no domain to guess from at all.
 *
 * The candidates are deliberately a list and never an automatic fill. Four
 * NetXL companies share one address in Guildford; the trading company, its
 * distributor and two holding companies are told apart by their number and
 * nothing else, and a form that picked one would be right about a quarter of
 * the time and silent about it.
 *
 * Sits below `ExistingCompanies`, which answers the prior question -- do we
 * already hold this company -- and must not be confused with it. That one looks
 * inward at our own records; this one looks outward at the register.
 */
export function CompanyAutofill({
  domain,
  name,
  form,
  onApply,
}: {
  domain: string;
  /** What is in the name field, which the register is searched for directly. */
  name: string;
  form: CompanyWrite;
  onApply: (patch: Partial<CompanyWrite>) => void;
}) {
  const debouncedDomain = useDebounced(domain.trim(), 400);
  const debouncedName = useDebounced(name.trim(), 400);

  const suggestions = useQuery({
    queryKey: queryKeys.crmSuggest(debouncedDomain, debouncedName),
    queryFn: () => crmApi.suggest(debouncedDomain, debouncedName),
    /*
     * Either field can carry the question, so either can start the request.
     * A domain needs a dot before it can be one, and a name needs three
     * characters before the register stops answering with everything that
     * begins with them -- the same floor the server applies, mirrored here so
     * that a keystroke nobody can answer never becomes a request.
     */
    enabled: debouncedDomain.includes(".") || debouncedName.length >= 3,
    staleTime: 5 * 60_000,
  });

  const data = suggestions.data;
  if (!data) return null;

  const candidates = data.candidates ?? [];

  /* Only what the form has not already got. Overwriting something typed by
     hand with a bureau's guess is the one thing this must never do. */
  const offers = FIELDS.flatMap(([key, label]) => {
    const suggestion = data.fields[key];
    if (!suggestion) return [];
    if ((form[key] ?? "") !== "") return [];
    return [{ key, label, ...suggestion }];
  });

  if (offers.length === 0 && candidates.length === 0) {
    /*
     * The note first, because it is the more specific of the two and the one
     * that answers what just happened: "no company called qwertyuiopasdf"
     * follows a search that ran and found nothing, while
     * `lookupUnavailableReason` explains a search that could not be made at
     * all. Rendering only the second swallowed the first entirely -- the whole
     * panel disappeared as somebody typed, which reads as the feature being
     * broken rather than as the register having no such company.
     */
    const message = data.candidatesNote ?? data.lookupUnavailableReason;

    return message ? (
      <p className="sm:col-span-2 text-[11px] text-faint">{message}</p>
    ) : null;
  }

  return (
    <div className="sm:col-span-2 space-y-2 rounded-md border border-dashed border-line px-3 py-2">
      {candidates.length > 0 ? (
        <div>
          {data.candidatesNote ? (
            <p className="mb-1.5 text-[11px] text-faint">
              {data.candidatesNote}
            </p>
          ) : null}

          <ul className="space-y-1">
            {candidates.map((candidate) => (
              <li key={candidate.key}>
                <button
                  type="button"
                  onClick={() =>
                    onApply(candidate.fields as Partial<CompanyWrite>)
                  }
                  className="w-full rounded border border-line px-2 py-1.5 text-left hover:border-brand-300 hover:bg-surface"
                >
                  <span className="flex flex-wrap items-baseline justify-between gap-x-2">
                    <span className="text-xs text-content">
                      {candidate.name}
                    </span>
                    {candidate.companyNumber ? (
                      <span className="text-[11px] tabular-nums text-faint">
                        #{candidate.companyNumber}
                      </span>
                    ) : null}
                  </span>

                  {/* The line that tells four companies at one address apart.
                      Status first because a dissolved company is the one thing
                      here that should stop somebody mid-click. */}
                  <span className="mt-0.5 flex flex-wrap gap-x-1.5 text-[11px] text-muted">
                    {candidate.status ? (
                      <span
                        className={
                          isActive(candidate.status)
                            ? "text-muted"
                            : "text-sla-warning"
                        }
                      >
                        {candidate.status}
                      </span>
                    ) : null}
                    {candidate.city ? <span>· {candidate.city}</span> : null}
                    {candidate.vatNumber ? (
                      <span>· {candidate.vatNumber}</span>
                    ) : null}
                  </span>

                  {/* Said on the row rather than by hiding it: somebody about to
                      create a company we already hold needs telling, and a row
                      that vanished would read as the register not having it. */}
                  {candidate.linkedTo ? (
                    <span className="mt-0.5 block text-[11px] text-sla-warning">
                      Already linked to {candidate.linkedTo.name} (#
                      {candidate.linkedTo.id})
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {offers.length > 0 ? (
        <div>
          <div className="mb-1.5 flex items-baseline gap-2">
            <span className="text-[11px] uppercase tracking-wide text-faint">
              Suggested from {data.domain}
            </span>
            <button
              type="button"
              onClick={() =>
                onApply(
                  Object.fromEntries(
                    offers.map((offer) => [offer.key, offer.value]),
                  ) as Partial<CompanyWrite>,
                )
              }
              className="text-[11px] text-brand-600 hover:text-brand-700"
            >
              Use all
            </button>
          </div>

          <ul className="flex flex-wrap gap-1">
            {offers.map((offer) => (
              <li key={offer.key}>
                <button
                  type="button"
                  title={offer.note}
                  onClick={() => onApply({ [offer.key]: offer.value })}
                  className="rounded-full border border-line px-2 py-0.5 text-[11px] text-muted hover:border-brand-300 hover:text-brand-600 dark:hover:text-brand-400"
                >
                  {offer.label}:{" "}
                  <span className="text-content">{offer.value}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {data.lookupUnavailableReason ? (
        <p className="text-[11px] text-faint">{data.lookupUnavailableReason}</p>
      ) : null}
    </div>
  );
}

/**
 * Whether a register's status word means "still trading".
 *
 * Mirrors the server's `isActiveStatus`, and mirrored rather than shared for
 * the same reason the sales-desk slugs are: this decides a colour, and a colour
 * that waits on the API to be redeployed is worse than one that is two lines
 * long in both places. Anything unrecognised is drawn as a warning, which is
 * the safe direction -- an unknown status is exactly what somebody should look
 * at twice.
 */
function isActive(status: string): boolean {
  return status.toLowerCase().startsWith("active");
}

/** The fields a domain can speak to, in the order the form asks for them. */
const FIELDS: [
  keyof CompanySuggestions["fields"] & keyof CompanyWrite,
  string,
][] = [
  ["name", "Name"],
  ["companyNumber", "Company number"],
  ["vatNumber", "VAT"],
  ["city", "City"],
  ["region", "Region"],
  ["customerType", "Type"],
  ["lifecycleStage", "Stage"],
];

/**
 * A value that stops changing before it is used.
 *
 * The lookup may reach a third party, so it must not fire per keystroke --
 * unlike the duplicate check beside it, which is a local index hit and is
 * deliberately instant.
 */
function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return settled;
}
